import { useState } from 'react';
import { seatTypes } from '../utils/seatUtils';

/**
 * Wider, tabbed left sidebar — zones first, templates second.
 */
export default function EditorSidebar({
  useZones,
  zoneMode,
  selectedZone,
  currentZones = [],
  seats = [],
  isCreatingZone,
  setIsCreatingZone,
  newZone,
  setNewZone,
  selectedSeatType,
  setSelectedSeatType,
  setAddSeatMode,
  fillZoneWithSeats,
  fillZoneTribune,
  exitZone,
  enterZone,
  onSeatsCreate,
  setSeats,
  saveToHistory,
  zoneDrawingMode,
  setZoneDrawingMode,
  setActiveTool,
  activeTool,
  seatTool = 'paint',
  applySeatTool,
  backgroundImage,
  onBackgroundImageChange,
  onBackgroundClear,
  backgroundOpacity = 0.85,
  onBackgroundOpacityChange,
  gridCols,
  gridRows,
  setGridCols,
  setGridRows,
  onPrevious,
  onNext,
  naming,
  applyNaming,
  aisleColInput = '',
  setAisleColInput,
  emptyColumn,
  zoom = 1,
  zoomIn,
  zoomOut,
  resetZoom,
  isAreaLayout = false,
  layoutTemplates = [],
  activeTemplateId = 'blank',
  applyLayoutTemplate,
  highlightedZone,
  setHighlightedZone,
  deleteZone,
  copyZone,
  pasteZone,
  duplicateZone,
  updateZoneFields,
  zoneClipboardReady = false,
  targetCapacity = '',
  setTargetCapacity,
  distributeLayoutCapacity,
  getZoneMaxSlots,
  layoutMaxSlots = 0,
  layoutAssignedCapacity = 0,
  showAllZoneSeats = false,
  setShowAllZoneSeats,
  showAlert,
}) {
  const [tab, setTab] = useState(useZones ? 'zones' : 'tools');
  const [ringA, setRingA] = useState(6);
  const [ringB, setRingB] = useState(8);
  const [ringC, setRingC] = useState(10);
  const [includeVip, setIncludeVip] = useState(true);
  const [includeStage, setIncludeStage] = useState(true);

  const active = highlightedZone || selectedZone;
  const zoneSeatCount = (id) => seats.filter((s) => s.zoneId === id).length;
  const activeMax = active && getZoneMaxSlots ? getZoneMaxSlots(active) : 0;
  const activePlaced = active ? zoneSeatCount(active.id) : 0;
  const activeAssigned = Number(active?.capacity) || 0;
  const targetNum = Math.max(0, Math.floor(Number(targetCapacity) || 0));
  const remainingToAssign = Math.max(0, targetNum - layoutAssignedCapacity);

  const startDrawZone = (mode = 'polygon') => {
    setZoneDrawingMode(mode);
    setIsCreatingZone(true);
    setActiveTool && setActiveTool('zone-create');
    setNewZone({
      ...newZone,
      name: newZone.name || `Blok ${currentZones.length + 1}`,
      color: newZone.color || (currentZones.length % 2 === 0 ? '#0F766E' : '#E11D48'),
    });
    setTab('zones');
  };

  const tabs = [
    useZones && { id: 'zones', label: 'Zone’lar' },
    useZones && { id: 'sablon', label: 'Şablon' },
    { id: 'tools', label: 'Araçlar' },
    { id: 'gorsel', label: 'Görsel' },
  ].filter(Boolean);

  return (
    <aside className="w-[22rem] flex-shrink-0 h-full bg-white border-r border-gray-200 flex flex-col text-gray-800 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-900">Layout Editör</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {isAreaLayout ? 'Alan / blok planı' : 'Zone + koltuk'}
            </div>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={zoomOut}
              className="w-7 h-7 rounded-md text-sm hover:bg-white"
              title="Uzaklaştır"
            >
              −
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="min-w-[2.75rem] h-7 px-1 rounded-md text-[11px] tabular-nums hover:bg-white"
            >
              {Math.round((zoom || 1) * 100)}%
            </button>
            <button
              type="button"
              onClick={zoomIn}
              className="w-7 h-7 rounded-md text-sm hover:bg-white"
              title="Yakınlaştır"
            >
              +
            </button>
          </div>
        </div>

        <div className="mt-3 flex gap-1 bg-gray-100 p-1 rounded-lg">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                tab === t.id
                  ? 'bg-white text-[#AE256D] shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* —— ZONES —— */}
        {tab === 'zones' && useZones && (
          <>
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Bloklar ({currentZones.length})
                </h3>
                {zoneMode === 'zone-edit' && selectedZone && (
                  <div
                    className="rounded-xl border px-3 py-2 text-[11px]"
                    style={{
                      borderColor: selectedZone.color || '#AE256D',
                      backgroundColor: `${selectedZone.color || '#AE256D'}14`,
                    }}
                  >
                    <div className="font-semibold text-gray-900">
                      Zone sayfası · {selectedZone.name}
                    </div>
                    <div className="text-gray-600 mt-0.5">
                      Zone çizgisi gizli — buraya koltuk düzenini çiz. Diğer zone’lar görünmez
                      (istersen “Tüm koltuklar”).
                    </div>
                  </div>
                )}

                {zoneMode === 'zone-edit' && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAllZoneSeats && setShowAllZoneSeats(!showAllZoneSeats)}
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                        showAllZoneSeats
                          ? 'bg-[#AE256D] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Diğer zone koltuklarını da göster / gizle"
                    >
                      {showAllZoneSeats ? 'Sadece bu zone' : 'Tüm koltuklar'}
                    </button>
                    <button
                      type="button"
                      onClick={exitZone}
                      className="text-[10px] text-blue-600 hover:underline"
                    >
                      ← Genel bakış
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { id: 'polygon', label: 'Serbest' },
                  { id: 'rectangle', label: 'Kutu' },
                  { id: 'circle', label: 'Daire' },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => startDrawZone(m.id)}
                    className={`py-2 rounded-lg text-[11px] font-medium border ${
                      isCreatingZone && zoneDrawingMode === m.id
                        ? 'bg-[#AE256D] text-white border-[#AE256D]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-[#AE256D]'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {isCreatingZone && (
                <div className="rounded-lg border border-pink-200 bg-pink-50 p-2.5 space-y-2">
                  <div className="text-[11px] font-semibold text-[#AE256D]">Çizim aktif</div>
                  <input
                    type="text"
                    value={newZone.name}
                    onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
                    placeholder="İsim (A7, VIP…)"
                    className="w-full text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                  />
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newZone.color}
                      onChange={(e) => setNewZone({ ...newZone, color: e.target.value })}
                      className="h-9 w-12 rounded border border-gray-300"
                    />
                    <input
                      type="number"
                      value={newZone.capacity}
                      onChange={(e) => setNewZone({ ...newZone, capacity: e.target.value })}
                      placeholder="Kapasite"
                      className="flex-1 text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                    />
                  </div>
                  <p className="text-[10px] text-gray-600">
                    {zoneDrawingMode === 'polygon'
                      ? 'Canvas’ta tıkla → nokta ekle. Enter veya ilk noktaya tık = bitir.'
                      : 'Canvas’ta sürükleyerek çiz.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingZone(false);
                      setActiveTool && setActiveTool('zone-select');
                    }}
                    className="w-full py-1.5 rounded-lg text-xs text-red-600 bg-white border border-red-200"
                  >
                    Çizimi iptal
                  </button>
                </div>
              )}

              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={!active || active.isStage}
                  onClick={() => copyZone && copyZone(active)}
                  className="flex-1 py-2 rounded-lg text-[11px] font-medium border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50"
                >
                  Kopyala
                </button>
                <button
                  type="button"
                  disabled={!zoneClipboardReady}
                  onClick={() => pasteZone && pasteZone()}
                  className="flex-1 py-2 rounded-lg text-[11px] font-medium border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50"
                >
                  Yapıştır
                </button>
                <button
                  type="button"
                  disabled={!active || active.isStage}
                  onClick={() => duplicateZone && duplicateZone(active)}
                  className="flex-1 py-2 rounded-lg text-[11px] font-medium border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50"
                >
                  Çoğalt
                </button>
              </div>
              <p className="text-[10px] text-gray-400">
                Sürükle = taşı · Tık = zone’a gir · Ctrl+C/V/D · Delete = sil
              </p>
            </section>

            <section className="rounded-xl border border-[#AE256D]/20 bg-pink-50/60 p-3 space-y-2">
              <div className="text-xs font-semibold text-gray-900">Genel kapasite</div>
              <p className="text-[10px] text-gray-600 leading-snug">
                Toplam koltuk sayısını gir → zone’lara eşit bölünür ve her zone o kadar koltuk
                sığacak şekilde büyütülür. Sonra seçtiğin zone’dan elle ince ayar yapabilirsin.
              </p>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={targetCapacity}
                  onChange={(e) => setTargetCapacity && setTargetCapacity(e.target.value)}
                  placeholder="Örn. 1200"
                  className="flex-1 text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                />
                <button
                  type="button"
                  onClick={() => distributeLayoutCapacity && distributeLayoutCapacity()}
                  className="px-3 py-2 rounded-lg bg-[#AE256D] text-white text-[11px] font-semibold whitespace-nowrap"
                >
                  Eşit dağıt + büyüt
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                  <div className="text-gray-400">Hedef</div>
                  <div className="font-semibold tabular-nums text-gray-900">{targetNum || '—'}</div>
                </div>
                <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                  <div className="text-gray-400">Atanan</div>
                  <div className="font-semibold tabular-nums text-gray-900">
                    {layoutAssignedCapacity}
                  </div>
                </div>
                <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                  <div className="text-gray-400">Layout max</div>
                  <div className="font-semibold tabular-nums text-gray-900">{layoutMaxSlots}</div>
                </div>
                <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                  <div className="text-gray-400">Kalan</div>
                  <div
                    className={`font-semibold tabular-nums ${
                      remainingToAssign > 0 ? 'text-amber-700' : 'text-green-700'
                    }`}
                  >
                    {remainingToAssign}
                  </div>
                </div>
              </div>
              {targetNum > 0 && layoutMaxSlots > 0 && targetNum > layoutMaxSlots * 1.15 && (
                <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                  Hedef hâlâ max’tan büyük — “Eşit dağıt + büyüt” ile zone’ları büyüt veya grid’i
                  kontrol et.
                </p>
              )}
            </section>

            {active && !active.isStage && (
              <section className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
                <div className="text-xs font-semibold text-gray-800">Seçili zone düzenle</div>
                <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                  <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                    <div className="text-gray-400">Sığar</div>
                    <div className="font-semibold tabular-nums text-[#AE256D]">{activeMax}</div>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                    <div className="text-gray-400">Atanan</div>
                    <div className="font-semibold tabular-nums">{activeAssigned || '—'}</div>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-200 px-2 py-1.5">
                    <div className="text-gray-400">Dolu</div>
                    <div className="font-semibold tabular-nums">{activePlaced}</div>
                  </div>
                </div>
                <label className="block text-[10px] text-gray-500">
                  İsim
                  <input
                    type="text"
                    value={active.name || ''}
                    onChange={(e) =>
                      updateZoneFields && updateZoneFields(active.id, { name: e.target.value })
                    }
                    className="mt-1 w-full text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                  />
                </label>
                <div className="flex gap-2">
                  <label className="text-[10px] text-gray-500">
                    Renk
                    <input
                      type="color"
                      value={active.color || '#0F766E'}
                      onChange={(e) =>
                        updateZoneFields && updateZoneFields(active.id, { color: e.target.value })
                      }
                      className="mt-1 block h-9 w-12 rounded border border-gray-300"
                    />
                  </label>
                  <label className="flex-1 text-[10px] text-gray-500">
                    Kapasite (plan)
                    <input
                      type="number"
                      min="0"
                      value={active.capacity ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          updateZoneFields && updateZoneFields(active.id, { capacity: '' });
                          return;
                        }
                        const n = Math.max(0, Math.floor(Number(raw) || 0));
                        updateZoneFields && updateZoneFields(active.id, { capacity: n });
                      }}
                      className="mt-1 w-full text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                    />
                  </label>
                </div>
                <p className="text-[10px] text-gray-500">
                  Şekil tahmini ~{activeMax} koltuk. Zone’a girince istediğin kadar koltuk
                  ekleyebilirsin — bu sayı sadece plandır, eklemeyi kilitlemez.
                </p>
                <div className="flex gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => enterZone && enterZone(active)}
                    className="flex-1 py-2 rounded-lg bg-[#AE256D] text-white text-[11px] font-semibold"
                  >
                    Zone’a gir / koltuk ekle
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteZone && deleteZone(active.id)}
                    className="flex-1 py-2 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold border border-red-100"
                  >
                    Sil
                  </button>
                </div>
              </section>
            )}

            <section className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-0.5">
                Liste
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto pr-0.5">
                {currentZones.map((z) => {
                  const selected = active?.id === z.id;
                  const max = z.isStage ? 0 : getZoneMaxSlots ? getZoneMaxSlots(z) : 0;
                  const placed = zoneSeatCount(z.id);
                  const assigned = Number(z.capacity) || 0;
                  return (
                    <div
                      key={z.id}
                      className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                        selected
                          ? 'border-[#AE256D] bg-pink-50'
                          : 'border-gray-100 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <button
                        type="button"
                        className="flex-1 flex items-center gap-2 text-left min-w-0"
                        onClick={() => {
                          setHighlightedZone && setHighlightedZone(z);
                          // Keep overview so capacity planning stays visible; double path via "gir"
                        }}
                      >
                        <span
                          className="w-3 h-3 rounded flex-shrink-0"
                          style={{ backgroundColor: z.color }}
                        />
                        <span className="text-xs font-medium truncate">{z.name}</span>
                        <span className="text-[10px] text-gray-400 ml-auto tabular-nums whitespace-nowrap">
                          {z.isStage ? 'sahne' : `${placed}/${assigned || '—'} · max ${max}`}
                        </span>
                      </button>
                      {!z.isStage && (
                        <button
                          type="button"
                          title="Çoğalt"
                          onClick={() => duplicateZone && duplicateZone(z)}
                          className="text-[10px] px-1.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                        >
                          +
                        </button>
                      )}
                    </div>
                  );
                })}
                {currentZones.length === 0 && (
                  <p className="text-[11px] text-gray-500 text-center py-6">
                    Zone yok — Şablon sekmesinden yükle veya çiz.
                  </p>
                )}
              </div>
            </section>
          </>
        )}

        {/* —— TEMPLATES —— */}
        {tab === 'sablon' && useZones && (
          <>
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Hazır şablon
              </h3>
              <p className="text-[11px] text-gray-500 leading-snug">
                Yükledikten sonra Zone’lar sekmesinde kopyala / yapıştır / çoğalt / isim-renk
                değiştir.
              </p>
              <div className="space-y-1.5">
                {layoutTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => {
                      applyLayoutTemplate && applyLayoutTemplate(tpl.id);
                      setTab('zones');
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border transition-colors ${
                      activeTemplateId === tpl.id
                        ? 'border-[#AE256D] bg-pink-50 text-[#AE256D]'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-semibold">{tpl.name}</div>
                    {tpl.description && (
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                        {tpl.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2.5">
              <div className="text-sm font-semibold text-gray-900">Kendi amfini kur</div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                {[
                  ['A', ringA, setRingA],
                  ['B', ringB, setRingB],
                  ['C', ringC, setRingC],
                ].map(([letter, val, setVal]) => (
                  <label key={letter} className="text-gray-500">
                    {letter} adet
                    <input
                      type="number"
                      min={1}
                      max={24}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900"
                    />
                  </label>
                ))}
              </div>
              <div className="flex gap-4 text-[11px] text-gray-700">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={includeVip}
                    onChange={(e) => setIncludeVip(e.target.checked)}
                  />
                  VIP
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={includeStage}
                    onChange={(e) => setIncludeStage(e.target.checked)}
                  />
                  Sahne
                </label>
              </div>
              <button
                type="button"
                onClick={() => {
                  applyLayoutTemplate &&
                    applyLayoutTemplate('amphitheater', {
                      rings: [
                        { letter: 'A', count: Math.max(1, Number(ringA) || 1) },
                        { letter: 'B', count: Math.max(1, Number(ringB) || 1) },
                        { letter: 'C', count: Math.max(1, Number(ringC) || 1) },
                      ],
                      includeVip,
                      includeStage,
                    });
                  setTab('zones');
                }}
                className="w-full py-2.5 rounded-xl bg-[#AE256D] hover:bg-[#8B1E55] text-white text-sm font-semibold"
              >
                Amfi oluştur
              </button>
            </section>
          </>
        )}

        {/* —— TOOLS —— */}
        {tab === 'tools' && (
          <>
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Koltuk
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'paint', label: 'Boya' },
                  { id: 'box', label: 'Kutu doldur' },
                  { id: 'erase', label: 'Silgi' },
                  { id: 'select', label: 'Seç' },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applySeatTool && applySeatTool(t.id)}
                    className={`px-2 py-3 rounded-xl text-sm font-semibold ${
                      seatTool === t.id
                        ? 'bg-[#AE256D] text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <select
                value={selectedSeatType}
                onChange={(e) => setSelectedSeatType(e.target.value)}
                className="w-full text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
              >
                {seatTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {useZones && zoneMode !== 'zone-edit' && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                  Koltuk eklemek için önce bir zone’a gir (tıkla veya “Zone’a gir”).
                </p>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Gezinme
              </h3>
              <button
                type="button"
                onClick={() => {
                  setActiveTool && setActiveTool('hand');
                  setAddSeatMode && setAddSeatMode(false);
                }}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold ${
                  activeTool === 'hand' ? 'bg-[#AE256D] text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                El — sürükleyerek gez
              </button>
            </section>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Satır / kolon isimleri
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="text-gray-500">
                  Satır başlangıç
                  <input
                    type="text"
                    defaultValue={naming?.rowStart || 'A'}
                    key={`rowStart-${naming?.rowStart}`}
                    onBlur={(e) =>
                      applyNaming &&
                      applyNaming(
                        { ...naming, rowStart: (e.target.value || 'A').toUpperCase() },
                        'Satir baslangici degisti',
                      )
                    }
                    className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900 uppercase"
                  />
                </label>
                <label className="text-gray-500">
                  No başlangıç
                  <input
                    type="number"
                    min={1}
                    defaultValue={naming?.colStart ?? 1}
                    key={`colStart-${naming?.colStart}`}
                    onBlur={(e) =>
                      applyNaming &&
                      applyNaming(
                        { ...naming, colStart: Math.max(1, Number(e.target.value) || 1) },
                        'Koltuk no baslangici degisti',
                      )
                    }
                    className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                  />
                </label>
              </div>
              <div className="flex gap-1.5">
                <input
                  type="number"
                  min={1}
                  value={aisleColInput}
                  onChange={(e) => setAisleColInput && setAisleColInput(e.target.value)}
                  placeholder="Kolon no"
                  className="flex-1 text-xs bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                />
                <button
                  type="button"
                  onClick={() => {
                    const n = Number(aisleColInput);
                    if (!n || n < 1) {
                      showAlert && showAlert('Kolon numarası gir');
                      return;
                    }
                    emptyColumn && emptyColumn(n - 1);
                    setAisleColInput && setAisleColInput('');
                  }}
                  className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-xs text-white font-medium"
                >
                  Boşalt
                </button>
              </div>
            </section>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Canvas boyutu
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="text-gray-500">
                  Kolon
                  <input
                    type="number"
                    min={10}
                    max={400}
                    value={gridCols}
                    onChange={(e) =>
                      setGridCols(Math.max(10, Math.min(400, Number(e.target.value) || 10)))
                    }
                    className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                  />
                </label>
                <label className="text-gray-500">
                  Satır
                  <input
                    type="number"
                    min={10}
                    max={300}
                    value={gridRows}
                    onChange={(e) =>
                      setGridRows(Math.max(10, Math.min(300, Number(e.target.value) || 10)))
                    }
                    className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-gray-900"
                  />
                </label>
              </div>
            </section>
          </>
        )}

        {/* —— IMAGE —— */}
        {tab === 'gorsel' && (
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Zemin görseli
            </h3>
            <label className="flex flex-col items-center justify-center gap-2 px-3 py-8 rounded-xl border border-dashed border-gray-300 hover:border-[#AE256D] cursor-pointer bg-gray-50">
              <span className="text-sm text-gray-700 font-medium">
                {backgroundImage ? 'Görseli değiştir' : 'Plan görseli yükle'}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!file.type.startsWith('image/')) {
                    showAlert && showAlert('Görsel dosyası seçin');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () =>
                    onBackgroundImageChange && onBackgroundImageChange(String(reader.result));
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }}
              />
            </label>
            {backgroundImage && (
              <>
                <div className="rounded-xl overflow-hidden border border-gray-200 h-28 bg-gray-50">
                  <img src={backgroundImage} alt="Plan" className="w-full h-full object-contain" />
                </div>
                <label className="block text-[11px] text-gray-500">
                  Opaklık: {Math.round(backgroundOpacity * 100)}%
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={Math.round(backgroundOpacity * 100)}
                    onChange={(e) =>
                      onBackgroundOpacityChange &&
                      onBackgroundOpacityChange(Number(e.target.value) / 100)
                    }
                    className="w-full mt-1"
                  />
                </label>
                <button
                  type="button"
                  onClick={onBackgroundClear}
                  className="w-full text-xs py-2 rounded-lg bg-red-50 text-red-600"
                >
                  Görseli kaldır
                </button>
              </>
            )}
          </section>
        )}
      </div>

      <div className="border-t border-gray-200 p-3 flex gap-2 bg-white">
        {onPrevious && (
          <button
            type="button"
            onClick={onPrevious}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Geri
          </button>
        )}
        {onNext && (
          <button
            type="button"
            onClick={onNext}
            className="flex-1 py-2.5 rounded-xl bg-[#AE256D] text-white text-xs font-semibold hover:bg-[#8B1E55]"
          >
            İleri
          </button>
        )}
      </div>
    </aside>
  );
}
